import { defaults, get, isObject } from 'lodash-es';
import { getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { computed, provide, ref, useSlots } from 'vue';
import { useFieldError } from '@phoenix/composables/fieldError';

export const props = {
  attrs: {
    type: Object,
    required: true,
    validator(field) {
      return 'id' in field;
    },
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
  shouldDisplayCharLeft: {
    type: Boolean,
    default: false,
  },
};

export const useFormGroup = (props) => {
  const slots = useSlots();

  const currentLanguage = ref(window.Phoenix.laravelLanguage);
  const currentCountry = ref(window.Phoenix.currentCountry);

  const checkMethods = {
    checkFeatureFlag: (featureFlag) => getFeatureFlag(featureFlag),

    checkIsLanguage: (language) => {
      if (Array.isArray(language)) {
        return language.includes(currentLanguage.value);
      }
      return currentLanguage.value === language;
    },

    checkIsCountry: (country) => {
      if (Array.isArray(country)) {
        return country.includes(currentCountry.value);
      }
      return currentCountry.value === country;
    },
  };

  const field = computed(() => {
    const defaultField = {
      label: null,
      help: null,
      tooltip: null,
      required: false,
      negative: false,
      sensitive: false,
    };

    const field = defaults(props.attrs, defaultField);

    const required = field.required;
    if (isObject(required)) {
      const method = get(required, 'method');
      const params = get(required, 'params', []);
      const result = get(required, 'result', true);

      field.required = checkMethods[method](...params) === result;
    }

    const pattern = field.pattern;
    if (isObject(pattern)) {
      const method = get(pattern, 'method');
      const params = get(pattern, 'params', []);
      const result = get(pattern, 'result', true);

      field.pattern =
        checkMethods[method](...params) === result ? get(pattern, 'value', null) : get(pattern, 'elseValue', null);
    }

    if (typeof field.pattern === 'string' && field.pattern.length > 0) {
      const existingRules = field.rules || {};
      if (existingRules.regex === undefined) {
        existingRules.regex = new RegExp(field.pattern);
        field.rules = existingRules;
      }
    }

    return field;
  });

  const { displayedErrors, isInvalid } = useFieldError(field);

  const isHidden = computed(() => field.value.type === 'hidden');

  const isCheckbox = computed(() => field.value.type === 'checkbox');

  const isCheckboxList = computed(() => field.value.type === 'checkboxList');

  const isDate = computed(() => field.value.type === 'date');

  const isPhone = computed(() => field.value.type === 'phone');

  const isSelect = computed(() => field.value.type === 'select');

  const formGroupClasses = computed(() => (isHidden.value ? '' : 'form-group'));

  const labelClasses = computed(() => ['field-label', { 'label--negative': field.value.negative }]);

  const labelId = computed(() => `${field.value.id}-label`);

  const errorId = computed(() => `${field.value.id}-error`);

  const helpId = computed(() => `${field.value.id}-help`);

  const fieldUniqueId = computed(() => field.value.uniqueId || field.value.id);

  const isSensitive = computed(() => field.value.sensitive === true);

  const _shouldDisplayCharLeft = computed(() => props.shouldDisplayCharLeft && field.value.length);

  const charactersLeft = computed(() => Math.max(0, field.value.length - field.value.value.length));

  const charLeftClasses = computed(() => (field.value.type === 'textarea' ? 'text-end text-sm' : 'field-help'));

  const hasHelpSlot = computed(() => !!slots.help);

  const hasLegendSlot = computed(() => !!slots.legend);

  const getField = () => field.value;

  provide('getFormGroupField', getField);
  provide('showLabel', props.showLabel);

  return {
    _shouldDisplayCharLeft,
    charLeftClasses,
    charactersLeft,
    currentCountry,
    currentLanguage,
    displayedErrors,
    errorId,
    field,
    fieldUniqueId,
    formGroupClasses,
    hasHelpSlot,
    hasLegendSlot,
    helpId,
    isCheckbox,
    isCheckboxList,
    isDate,
    isHidden,
    isInvalid,
    isPhone,
    isSelect,
    isSensitive,
    labelClasses,
    labelId,
  };
};
